import { take } from 'rxjs/operators';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { assetColumnsTranslationKey } from '@/shared/constants';
import { KeyValue } from '@angular/common';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { ColumnPreferenceService } from '../services/column-preference.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-assets-columns-dropdown',
  templateUrl: './assets-columns-dropdown.component.html',
  styleUrls: ['./assets-columns-dropdown.component.scss'],
})
export class AssetsColumnsDropdownComponent implements OnInit {
  @Input() keys: { [x: string]: boolean } = {};
  @Input() isBusy = false;
  @Input() isCardView: boolean = false;

  @Output() keysChange = new EventEmitter<{ [x: string]: boolean }>();
  @Output() isBusyChange = new EventEmitter<boolean>();
  @ViewChild(NgbDropdown) dropdown!: NgbDropdown;

  columnsTranslationKey = assetColumnsTranslationKey;

  // US1: Track mandatory columns
  private mandatoryColumnIds: string[] = [];

  // T057: Ordered columns for drag-drop list
  orderedColumns: { key: string; value: boolean }[] = [];

  // Snapshots taken when the dropdown opens, used to revert on cancel
  private keysSnapshot: { [x: string]: boolean } = {};
  private orderedColumnsSnapshot: { key: string; value: boolean }[] = [];

  constructor(
    private readonly toasterService: ToasterService,
    private columnPreferenceService: ColumnPreferenceService,
    private confirmationService: ConfirmationService
  ) {}

  // T057: Check if column is mandatory (cannot be reordered)
  isMandatoryColumn(columnId: string): boolean {
    return this.mandatoryColumnIds.includes(columnId);
  }

  // T057: Handle column drop event for reordering
  onColumnDrop(event: CdkDragDrop<string[]>) {
    const movedColumn = this.orderedColumns[event.previousIndex];

    // Prevent reordering mandatory columns
    if (this.isMandatoryColumn(movedColumn.key)) {
      this.toasterService.warn('RegisterService::MandatoryColumnCannotReorder');
      return;
    }

    // Update local array
    moveItemInArray(this.orderedColumns, event.previousIndex, event.currentIndex);

    // Extract column IDs in new order
    const newColumnOrder = this.orderedColumns.map(col => col.key);

    // Update column order via service
    this.columnPreferenceService.updateColumnOrder(newColumnOrder);

    // Emit keys change for backward compatibility
    const updatedKeys: { [key: string]: boolean } = {};
    this.orderedColumns.forEach(col => {
      updatedKeys[col.key] = this.keys[col.key];
    });
    this.keysChange.emit(updatedKeys);
  }

  ngOnInit() {
    // T039: Subscribe to preferences from ColumnPreferenceService
    this.columnPreferenceService.loadPreferences().then(() => {
      this.columnPreferenceService.getPreferences().pipe(take(1)).subscribe(prefs => {
        if (prefs) {
          // T041: Get mandatory column IDs
          this.mandatoryColumnIds = this.columnPreferenceService.getMandatoryColumnIds(prefs);

          // Get ordered column IDs from the new structure
          const orderedColumnIds = this.columnPreferenceService.getOrderedColumnIds(prefs);

          // Get all column IDs from metadata
          const allColumnIds = Object.keys(this.columnPreferenceService.getCurrentPreferences()?.columns || {});

          // Convert to keys object for backward compatibility - include ALL columns
          const keysObject: { [key: string]: boolean } = {};
          allColumnIds.forEach(colId => {
            keysObject[colId] = orderedColumnIds.includes(colId);
          });
          this.keysChange.emit(keysObject);

          // T057: Initialize orderedColumns with ALL columns from metadata
          // First add visible columns in order from preferences
          const visibleColumns = orderedColumnIds.map(colId => ({
            key: colId,
            value: true
          }));

          // Then add hidden columns (columns in metadata but not in preferences)
          const hiddenColumns = allColumnIds
            .filter(colId => !orderedColumnIds.includes(colId))
            .map(colId => ({
              key: colId,
              value: false
            }));

          // Combine visible and hidden columns
          this.orderedColumns = [...visibleColumns, ...hiddenColumns];
        }
      });
    });

    // Load current preferences if needed
  }

  onDropdownOpenChange(isOpen: boolean) {
    if (isOpen) {
      // Snapshot current state so cancel can revert
      this.keysSnapshot = { ...this.keys };
      this.orderedColumnsSnapshot = this.orderedColumns.map(col => ({ ...col }));
    }
  }

  onCancel() {
    // Revert keys to snapshot
    Object.keys(this.keysSnapshot).forEach(k => {
      this.keys[k] = this.keysSnapshot[k];
    });

    // Revert orderedColumns to snapshot
    this.orderedColumns = this.orderedColumnsSnapshot.map(col => ({ ...col }));

    // Restore service in-memory cache to the last persisted state
    this.columnPreferenceService.loadPreferences();

    // Emit restored keys so the parent datatable reflects the reverted state
    this.keysChange.emit({ ...this.keys });

    this.dropdown.close();
  }

  originalOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return 0;
  };

  onSelect(event: any, item: { key: string; value: boolean }) {
    const isChecking = event.target.checked;

    // T041: Prevent hiding mandatory columns (e.g., primaryID)
    if (!isChecking && this.mandatoryColumnIds.includes(item.key)) {
      this.toasterService.warn('RegisterService::CannotHideMandatoryColumn');
      event.target.checked = true; // Revert checkbox
      return;
    }

    // T041: Auto-select primaryID if all columns are being unchecked
    const wouldBeAllUnchecked = Object.keys(this.keys).every(
      key => key === item.key ? !isChecking : !this.keys[key]
    );
    if (wouldBeAllUnchecked && !isChecking) {
      this.toasterService.warn('RegisterService::AtLeastOneColumnRequired');
      this.keys['primaryID'] = true;
      event.target.checked = item.key === 'primaryID'; // Keep primaryID checked
    } else {
      this.keys[item.key] = isChecking;
    }

    // T040: Update local cache via ColumnPreferenceService
    this.columnPreferenceService.updateColumnVisibility(item.key, isChecking);

    // Reorder: selected columns first, then unselected
    this.reorderColumns();
  }

  /**
   * Reorder orderedColumns so checked items appear first, unchecked at the bottom.
   * Preserves relative order within each group.
   */
  private reorderColumns(): void {
    const selected = this.orderedColumns.filter(col => this.keys[col.key]);
    const unselected = this.orderedColumns.filter(col => !this.keys[col.key]);
    this.orderedColumns = [...selected, ...unselected];
  }

  changePreferences() {
    this.isBusyChange.emit(true);

    // T042: Call savePreferences from ColumnPreferenceService
    this.columnPreferenceService.savePreferences()
      .then(() => {
        // T043: Success toast notification
        this.toasterService.success('ServicingService::SuccessfullySaved');
        // Emit updated keys to parent so columns refresh after save
        this.keysChange.emit({ ...this.keys });
        this.onCancel();
      })
      .catch(() => {
        this.toasterService.error('RegisterService::FailedToSavePreferences');
      })
      .finally(() => {
        this.isBusyChange.emit(false);
      });
  }
}
